/**
 * Rules about the fields a form shows and demands.
 *
 * Lives outside the components so the form and the run guard cannot drift:
 * demanding a value for a field the form hid is a dead end for the user.
 */

import type { InputConfig } from '../types'

/** True when `hidden_when` takes this field off the form for the current values. */
export function isHidden(input: InputConfig, values: Record<string, unknown>): boolean {
  if (!input.hidden_when) return false

  return Object.entries(input.hidden_when).some(([field, hiddenValues]) => {
    // `?? ''` as in the app's shouldHideField: a null parent counts as "no value".
    return hiddenValues.includes(String(values[field] ?? ''))
  })
}

/** True for a value the user has not supplied — including a file with no bytes. */
export function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  // A cleared or stripped file keeps its metadata but carries no data — unless
  // it is a reference to an uploaded file, whose bytes live on the server.
  if (typeof value === 'object') {
    const file = value as { data?: unknown; file_id?: unknown }
    if (typeof file.file_id === 'string' && file.file_id !== '') return false
    return typeof file.data === 'string' && file.data === ''
  }
  return false
}

/** Labels of the required, visible fields still waiting for a value. */
export function missingRequired(
  inputs: InputConfig[],
  values: Record<string, unknown>
): string[] {
  return inputs
    .filter((input) => input.enabled && input.required && !isHidden(input, values))
    .filter((input) => isEmpty(values[input.name]))
    .map((input) => input.label)
}

/** Widgets the embed cannot render: they need a signed-in AgentBase user. */
export function isUnsupportedWidget(input: InputConfig): boolean {
  return input.widget === 'entitydropdown'
}

/**
 * Labels of visible, required fields the embedded form has no way to fill.
 * A run cannot succeed while one is empty, so the widget withholds the run
 * button and says whose move it is — the site owner, in the app's setup.
 */
export function unsupportedRequired(
  inputs: InputConfig[],
  values: Record<string, unknown>
): string[] {
  return inputs
    .filter((input) => input.enabled && input.required && !isHidden(input, values))
    .filter(isUnsupportedWidget)
    .filter((input) => isEmpty(values[input.name]))
    .map((input) => input.label)
}

export const UNSUPPORTED_FIELDS_TITLE = 'Appen kan ikke køres herfra'

/** Why the run is withheld, naming the fields and the fix (form-level, terminal). */
export function unsupportedFieldsReason(labels: string[]): string {
  const named = labels.map((label) => `"${label}"`).join(', ')
  const noun = labels.length === 1 ? 'Feltet' : 'Felterne'
  return (
    `${noun} ${named} kræver en bruger, der er logget ind i AgentBase. ` +
    'Skjul feltet, eller giv det en standardværdi under appens opsætning i AgentBase.'
  )
}

/** Title + reason as one sentence, for the error slot. */
export function unsupportedFieldsMessage(labels: string[]): string {
  return `${UNSUPPORTED_FIELDS_TITLE}. ${unsupportedFieldsReason(labels)}`
}
